import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";

const EditEmployeeDialog = ({ open, onClose, refetch, employee, hasHrmsIntegration = false }) => {
  const [updateEmployee, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  useEffect(() => {
    if (employee && open) {
      const email = Array.isArray(employee.emails)
        ? employee.emails.find((e) => e.type === "work")?.email || employee.emails[0]?.email || ""
        : employee.work_email || "";

      setFormData({
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: email,
      });
    }
  }, [employee, open]);

  const handleSave = async () => {
    if (!employee?.id) return;

    if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
      setSnackbarInfo({
        message: "First name and last name are required",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    try {
      const employeeEmail = Array.isArray(employee.emails)
        ? employee.emails.find((e) => e.type === "work")?.email || employee.emails[0]?.email || employee.work_email || ""
        : employee.work_email || "";

      if (!employeeEmail) {
        setSnackbarInfo({
          message: "Employee email is required",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      const response = await updateEmployee({
        variables: {
          input: [
            {
              id: employee.id,
              first_name: formData.first_name.trim(),
              middle_name: null,
              last_name: formData.last_name.trim(),
              name: `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim(),
              email: employeeEmail,
            },
          ],
        },
      });

      if (response?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: "Employee name updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) {
          await refetch();
        }
        onClose();
      } else {
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to update employee",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      setSnackbarInfo({
        message: error?.message || "Failed to update employee",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Edit Name
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {hasHrmsIntegration && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Employee name is managed by HRMS integration and cannot be edited manually.
              </Alert>
            )}
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              disabled={updating || hasHrmsIntegration}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              disabled={updating || hasHrmsIntegration}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              disabled
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || !formData.first_name?.trim() || !formData.last_name?.trim() || hasHrmsIntegration}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditEmployeeDialog;

